import { BookOpen, Check, Clock, Gift, Mic, PenLine, PlusCircle, Star, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { DailyChallenge, ChallengeType } from "@/hooks/use-daily-challenge";
import { getChallengeProgress } from "@/hooks/use-daily-challenge";
import { typeBodyClass, typeCaptionClass, typeTitleClass } from "@/lib/typography";

interface StreakChallengesProps {
  challenges: DailyChallenge[];
  isLoading?: boolean;
  claimingId?: string | null;
  onClaim?: (challengeId: string) => void;
  className?: string;
}

const challengeIcons: Record<ChallengeType, typeof Star> = {
  vocabulary_review: BookOpen,
  practice_topic: Mic,
  create_topic: PlusCircle,
  study_time: Clock,
  video_shadowing: Mic,
  video_dictation: PenLine,
};

const challengeRoutes: Record<ChallengeType, string> = {
  vocabulary_review: "/vocabulary/practice",
  practice_topic: "/topics",
  create_topic: "/my-topics",
  study_time: "/topics",
  video_shadowing: "/videos",
  video_dictation: "/videos",
};

/** English fallbacks for the `gamification.challenges.<type>` keys. */
const LABELS: Record<ChallengeType, string> = {
  vocabulary_review: "Review Vocabulary",
  practice_topic: "Practice a Topic",
  create_topic: "Create a Topic",
  study_time: "Study Time",
  video_shadowing: "Video Shadowing",
  video_dictation: "Video Dictation",
};

/**
 * Today's challenges — what they are, how far along, and where to go do one.
 *
 * ## The empty list says what to do, not that it is empty
 *
 * "Sign in to see your daily challenges!" was shown to signed-in learners whose
 * challenges simply had not been generated yet, so the most likely reading of an
 * empty list was "I have to go somewhere else" — while there was nowhere else
 * to go. It now says the list is not ready and to check back, which is true
 * and actionable.
 *
 * ## One accent, same as the milestone ladder
 *
 * Claimable is amber, done is quiet blue, pending is stone. Four competing
 * fills used to make a six-row list look like six unrelated notifications.
 */
export const StreakChallenges = ({
  challenges,
  isLoading = false,
  claimingId,
  onClaim,
  className,
}: StreakChallengesProps) => {
  const { t } = useTranslation();
  const completedCount = challenges.filter((c) => c.is_completed).length;
  const totalXp = challenges.reduce((sum, c) => sum + c.xp_reward, 0);

  const labelFor = (type: ChallengeType) =>
    t(`gamification.challenges.${type}`, {
      defaultValue: LABELS[type] ?? t("gamification.challenges.default", "Daily challenge"),
    });

  if (isLoading) {
    return (
      <div className={cn("space-y-2", className)} role="status" aria-busy="true">
        <span className="sr-only">{t("gamification.streak.loadingChallenges", "Loading challenges…")}</span>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-end justify-between gap-3">
        <h3 className={typeTitleClass}>
          {t("gamification.challenges.title", "Daily challenges")}
        </h3>
        {challenges.length > 0 && (
          <p className={cn("flex items-center gap-3 shrink-0", typeCaptionClass)}>
            <span className="flex items-center gap-1">
              <Target className="h-3 w-3" aria-hidden="true" />
              <span className="tabular-nums">
                {completedCount}/{challenges.length}
              </span>
            </span>
            <span className="flex items-center gap-1 font-semibold text-secondary">
              <Gift className="h-3 w-3" aria-hidden="true" />
              <span className="tabular-nums">{totalXp}</span>{" "}
              {t("gamification.streak.xpWord", "XP")}
            </span>
          </p>
        )}
      </div>

      {challenges.length === 0 ? (
        <div className="rounded-xl border border-stone-200/70 bg-stone-50/70 px-4 py-8 text-center dark:border-stone-800 dark:bg-stone-800/30">
          <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-stone-100 text-stone-400 dark:bg-stone-800 dark:text-stone-500">
            <Target className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className={typeBodyClass}>
            {t(
              "gamification.challenges.empty",
              "Today's challenges are being set up. Check back shortly.",
            )}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {challenges.map((challenge) => {
            const ChallengeIcon = challengeIcons[challenge.challenge_type] ?? Star;
            const isCompleted = challenge.is_completed;
            const isClaimed = challenge.is_claimed;
            const canClaim = isCompleted && !isClaimed;
            const progress = getChallengeProgress(challenge);

            return (
              <li
                key={challenge.id}
                className={cn(
                  "flex items-center gap-3 rounded-xl border p-3 transition-colors",
                  canClaim
                    ? "border-amber-200 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/20"
                    : isCompleted
                      ? "border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
                      : "border-stone-200/70 bg-stone-50/70 dark:border-stone-800/70 dark:bg-stone-800/30",
                )}
              >
                <span
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                    isClaimed
                      ? "bg-primary/10 text-primary"
                      : canClaim
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                        : "bg-stone-200/70 text-stone-500 dark:bg-stone-700/70 dark:text-stone-400",
                  )}
                  aria-hidden="true"
                >
                  {canClaim ? (
                    <Gift className="h-4 w-4" />
                  ) : isCompleted ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ChallengeIcon className="h-4 w-4" />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-stone-900 dark:text-stone-50">
                      {labelFor(challenge.challenge_type)}
                    </span>
                    <span className="shrink-0 text-[11px] font-semibold text-secondary">
                      +{challenge.xp_reward} XP
                    </span>
                  </div>

                  <div className="mt-1.5 flex items-center gap-2">
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={challenge.target_count}
                      aria-valuenow={challenge.current_count}
                      className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800"
                    >
                      <div
                        className={cn(
                          "h-full rounded-full transition-[width] duration-500",
                          isCompleted ? "bg-primary" : "bg-secondary",
                        )}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className={cn("shrink-0 tabular-nums", typeCaptionClass)}>
                      {challenge.current_count}/{challenge.target_count}
                      {challenge.challenge_type === "study_time" &&
                        ` ${t("gamification.challenges.minutesShort", "min")}`}
                    </span>
                  </div>
                </div>

                {canClaim ? (
                  <Button
                    size="sm"
                    className="h-8 shrink-0 rounded-lg px-3 text-xs"
                    disabled={claimingId === challenge.id}
                    onClick={() => onClaim?.(challenge.id)}
                  >
                    {t("common.actions.claim", "Claim")}
                  </Button>
                ) : (
                  !isCompleted && (
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                      className="h-8 w-8 shrink-0 rounded-lg p-0"
                    >
                      <Link
                        to={challengeRoutes[challenge.challenge_type] ?? "/"}
                        aria-label={t("gamification.challenges.open", "Go to this challenge")}
                      >
                        <ChallengeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                    </Button>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StreakChallenges;